import {
    onboardingBillingAddressSchema,
    type OnboardingBillingAddress,
} from '@houzy/schemas/partner-cockpit/onboarding';
import { PARTNER_COCKPIT_MAX_NAME_LENGTH } from '@houzy/schemas/partner-cockpit/user-management';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

type Field = keyof OnboardingBillingAddress;

// Filling either name requires the other, so leaving one of them flags the one still empty.
const isNameField = (key: Field): boolean => key === 'firstName' || key === 'lastName';

export type InvoiceDetailsFormProps = {
    initial: OnboardingBillingAddress;
    onChange: (details: OnboardingBillingAddress, valid: boolean) => void;
};

export function InvoiceDetailsForm({ initial, onChange }: InvoiceDetailsFormProps): ReactElement {
    const { t } = useTranslation();
    const [details, setDetails] = useState<OnboardingBillingAddress>(initial);
    const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});

    const parsed = onboardingBillingAddressSchema.safeParse(details);
    const fieldErrors = parsed.success ? undefined : z.flattenError(parsed.error).fieldErrors;

    const isTouched = (key: Field): boolean =>
        isNameField(key) ? Boolean(touched.firstName || touched.lastName) : Boolean(touched[key]);

    const errorFor = (key: Field): string | undefined => {
        if (!isTouched(key) || fieldErrors?.[key] === undefined) {
            return undefined;
        }
        if (isNameField(key)) {
            return t('ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.NAME_INCOMPLETE');
        }
        return t(key === 'billingEmail' ? 'ONBOARDING.ACCOUNT.ERRORS.EMAIL' : 'ONBOARDING.ACCOUNT.ERRORS.REQUIRED');
    };

    const update = <K extends Field>(key: K, value: OnboardingBillingAddress[K]): void => {
        const next = { ...details, [key]: value };
        setDetails(next);
        onChange(next, onboardingBillingAddressSchema.safeParse(next).success);
    };

    const markTouched = (key: Field): void => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const field = (
        key: Field,
        labelKey: string,
        extra?: { type?: string; autoComplete?: string; maxLength?: number },
    ): ReactElement => (
        <TextField
            label={t(labelKey)}
            fullWidth
            type={extra?.type}
            autoComplete={extra?.autoComplete}
            slotProps={{ htmlInput: { maxLength: extra?.maxLength } }}
            value={details[key] ?? ''}
            onChange={(event) => update(key, event.target.value)}
            onBlur={() => markTouched(key)}
            error={Boolean(errorFor(key))}
            helperText={errorFor(key)}
        />
    );

    return (
        <Stack spacing={2}>
            <Typography variant="h6">{t('ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.TITLE')}</Typography>

            {field('companyName', 'ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.COMPANY_NAME', {
                autoComplete: 'organization',
            })}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Stack sx={{ flex: 1 }}>
                    {field('firstName', 'ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.FIRST_NAME', {
                        autoComplete: 'given-name',
                        maxLength: PARTNER_COCKPIT_MAX_NAME_LENGTH,
                    })}
                </Stack>
                <Stack sx={{ flex: 1 }}>
                    {field('lastName', 'ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.LAST_NAME', {
                        autoComplete: 'family-name',
                        maxLength: PARTNER_COCKPIT_MAX_NAME_LENGTH,
                    })}
                </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Stack sx={{ flex: 3 }}>
                    {field('street', 'ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.BILLING_ADDRESS', {
                        autoComplete: 'address-line1',
                    })}
                </Stack>
                <Stack sx={{ flex: 1 }}>{field('nr', 'ONBOARDING.COMPANY.FIELDS.ADDRESS_NR')}</Stack>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Stack sx={{ flex: 1 }}>
                    {field('zip', 'ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.POSTCODE', {
                        autoComplete: 'postal-code',
                    })}
                </Stack>
                <Stack sx={{ flex: 1 }}>
                    {field('city', 'ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.CITY', {
                        autoComplete: 'address-level2',
                    })}
                </Stack>
            </Stack>

            {field('billingEmail', 'ONBOARDING.SUBSCRIPTION.INVOICE_DETAILS.BILLING_EMAIL', {
                type: 'email',
                autoComplete: 'email',
            })}
        </Stack>
    );
}
